import {
  Grid,
  Stack,
  Typography,
  Card,
  Divider,
  Tooltip,
  Link,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardActions,
  CardContent,
} from "@mui/material";
import React from "react";
import ProfileContainer from "./ProfileContainer";
import useAssociatedPartyStore from "../zstore/associatedPartyStore";
import { useShallow } from "zustand/react/shallow";
import {
  AssociatedPartiesHistoryItem,
  AssociatedPartiesItem,
  AssociatedPartyRoles,
} from "../api/transformers/associatedParty";
import Empty from "../components/Empty";
import { fullName, getEntityTypeCheckers } from "../utils/investor";
import { getAdviserLink } from "../utils/adviserLinks";
import { selectFromAmlStore } from "../amlSlice";
import { useSelector } from "react-redux";
import { groupBy, keys, sortBy, first, orderBy } from "lodash";
import { StatusNaming } from "../constant/amlConstant";
import { Marker } from "../api/transformers/marker";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import useBoolean from "../hooks/useBoolean";
import useAssociatedPartyHistoryStore from "../zstore/associatedPartyHistoryStore";
import { NavArrowRight } from "iconoir-react";
import { formatTimestamp } from "../utils/date";
import { useNavigate } from "react-router";

const entityTypeSelector = selectFromAmlStore((state) => state.entityTypes);

type AssociatedPartyProfileProps = {
  personId: string;
  markers: Marker[];
};

const AssociatedPartyProfile: React.FC<AssociatedPartyProfileProps> = ({
  markers,
  personId,
}) => {
  const { associatedParties, associatedPartyLoading } = useAssociatedPartyStore(
    useShallow((state) => ({
      associatedParties: state.data,
      associatedPartyLoading: state.loading,
    }))
  );

  const { amlDetail, amlDetailLoading } = useInvestorAmlDetailStore(
    useShallow((state) => ({
      amlDetail: state.data,
      amlDetailLoading: state.loading,
    }))
  );

  const {
    fetchData: fetchAssociatedPartyHistory,
    data: associatedPartyHistory,
    loading: associatedPartyHistoryLoading,
  } = useAssociatedPartyHistoryStore();

  const entityTypes = useSelector(entityTypeSelector);
  const entityTypeCheckers = getEntityTypeCheckers(entityTypes);
  const showSection =
    entityTypeCheckers.isCorporate(amlDetail?.entityType) ||
    entityTypeCheckers.isTrust(amlDetail?.entityType);
  const [
    historyModalOpen,
    { setTrue: showHistoryModal, setFalse: closeHistoryModal },
  ] = useBoolean(false);
  if (!showSection) {
    return null;
  }

  return (
    <ProfileContainer
      id="associated-parties-section"
      title="Associated Parties"
      loading={amlDetailLoading || associatedPartyLoading}
    >
      <Stack sx={{ marginBottom: "1%" }}>
        <div style={{ width: "100%" }}>
          <Link
            onClick={() => {
              showHistoryModal();
              fetchAssociatedPartyHistory(personId);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
              width: "fit-content",
              cursor: "pointer",
              float: "right",
            }}
          >
            View History <NavArrowRight />
          </Link>
        </div>
      </Stack>
      <AssociatedPartyList dataSource={associatedParties} markers={markers} />
      <AssociatedPartyHistoryModal
        open={historyModalOpen}
        onClose={closeHistoryModal}
        historyList={associatedPartyHistory}
        historyLoading={associatedPartyHistoryLoading}
      />
    </ProfileContainer>
  );
};

type AssociatedPartyHistoryProps = {
  open: boolean;
  onClose: () => void;
  historyList: AssociatedPartiesHistoryItem[];
  historyLoading: boolean;
};

const AssociatedPartyHistoryModal: React.FC<AssociatedPartyHistoryProps> = ({
  open,
  onClose,
  historyList,
  historyLoading,
}) => {
  const renderHistoryContent = (item: AssociatedPartiesHistoryItem) => {
    return (
      <TableRow key={item.investorId}>
        <TableCell>
          {item.timestamp ? formatTimestamp(item.timestamp).dateTime : "-"}
        </TableCell>
        <TableCell>{item.investorId}</TableCell>
        <TableCell>{fullName(item)}</TableCell>
        <TableCell>{item.roleDesc}</TableCell>
        <TableCell>{item.removed ? "Removed" : "Added"}</TableCell>
        <TableCell>{item.updatedBy || "-"}</TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="md">
      <DialogTitle sx={{ fontSize: "1.2rem" }}>
        Associated Party History
      </DialogTitle>
      <DialogContent sx={{ maxHeight: "500px" }}>
        <TableContainer
          component={Paper}
          sx={{ p: 0, width: "100%", maxHeight: 500 }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time</TableCell>
                <TableCell>From Person ID</TableCell>
                <TableCell>Party Name</TableCell>
                <TableCell>Role Type</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Updated By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      sx={{
                        minHeight: 300,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : historyList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="subtitle1" color="textSecondary">
                      No Data
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orderBy(historyList, ["timestamp"], ["desc"]).map(
                  renderHistoryContent
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

type AssociatedPartyListProps = {
  dataSource: AssociatedPartiesItem[];
  markers: Marker[];
};

const AssociatedPartyList: React.FC<AssociatedPartyListProps> = ({
  dataSource,
  markers,
}) => {
  const mergedAssociatedParties = aggregateRole(dataSource);
  return (
    <Stack direction="column" rowGap="6px">
      {dataSource.length === 0 ? (
        <Empty />
      ) : (
        mergedAssociatedParties.map((el: AssociatedPartiesItem) => (
          <AssociatedPartyItem
            key={el.investorId}
            data={el}
            markers={markers}
          />
        ))
      )}
    </Stack>
  );
};

type AssociatedPartyItemProps = {
  data: AssociatedPartiesItem;
  markers: Marker[];
};
const AssociatedPartyItem: React.FC<AssociatedPartyItemProps> = ({
  data,
  markers,
}) => {
  const itemTitleStyle = {
    fontStyle: "italic",
    fontWeight: "400",
    width: "32%",
  };
  const entityTypes = useSelector(entityTypeSelector);
  const entityTypeCheckers = getEntityTypeCheckers(entityTypes);
  const subType = entityTypeCheckers.getCategoryOfType<string>(
    data.entityType,
    "type"
  );
  const NO_MARKER_ASSIGNED = "-";
  const getMarkerDescription = (
    code: string,
    placeholder = NO_MARKER_ASSIGNED
  ) => {
    return markers.find((el) => el.code === code)?.description || placeholder;
  };

  const navigate = useNavigate();
  const handleViewAmlProfile = () => {
    navigate(`/investor-aml/${data.investorId}/profile`);
  };

  return (
    <Card key={data.investorId}>
      <CardContent>
        <Grid container>
          <Grid item sm={12} md={12}>
            <Typography variant="body2">
              <span style={{ fontWeight: "700", marginRight: "10px" }}>
                {fullName(data)}
              </span>
              <Tooltip title="View Investor Details">
                <Link
                  href={getAdviserLink("investorDetail", {
                    personId: data.investorId,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  underline="hover"
                  sx={{ fontStyle: "italic" }}
                >
                ({data.investorId})
                </Link>
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item sm={12} md={12} sx={{ display: "flex" }}>
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", fontWeight: "500" }}
            >
              {subType as string}
            </Typography>
            <Divider orientation="vertical" sx={{ marginLeft: "1%" }} />
            <div style={{ marginLeft: "1%" }}>
              {data?.roles &&
              data.roles.map((role: AssociatedPartyRoles) => (
                <Chip
                  key={role.code}
                  size="small"
                  label={role.desc}
                  sx={{ marginRight: "8px" }}
                />
              ))}
            </div>
          </Grid>

          <Grid
            item
            sm={12}
            md={12}
            sx={{ marginTop: "1rem", marginBottom: "1rem" }}
          >
            <Divider />
          </Grid>

          <Grid item sm={12} md={6} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={itemTitleStyle}>
            AML Status
            </Typography>
            <Typography variant="body2">
              {StatusNaming[data.amlStatus]}
            </Typography>
          </Grid>
          <Grid item sm={12} md={6} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={itemTitleStyle}>
            Sanctions
            </Typography>
            <Typography variant="body2">
              {getMarkerDescription(data.sanctionsStatus)}
            </Typography>
          </Grid>
          <Grid item sm={12} md={6} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={itemTitleStyle}>
            ID&V
            </Typography>
            <Typography variant="body2">
              {getMarkerDescription(data.idAndV)}
            </Typography>
          </Grid>
          <Grid item sm={12} md={6} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={itemTitleStyle}>
            Risk Rating
            </Typography>
            <Typography variant="body2">
              {getMarkerDescription(data.riskRatingStatus)}
            </Typography>
          </Grid>
          <Grid item sm={12} md={6} sx={{ display: "flex" }}>
            <Typography variant="h5" sx={itemTitleStyle}>
            PEP
            </Typography>
            <Typography variant="body2">
              {getMarkerDescription(data.pepStatus)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Button variant="outlined" color="primary" sx={{ borderRadius: "4px" }} onClick={handleViewAmlProfile}>
          View AML Profile
        </Button>
      </CardActions>
    </Card>
  );
};

function aggregateRole(associatedParties: AssociatedPartiesItem[]): any {
  const personGroups = groupBy(associatedParties, "investorId");
  return keys(personGroups).map((investorId) => {
    const group = personGroups[investorId];
    const orderedGroup = sortBy(group, "roleSeq");
    const initial = first(orderedGroup);
    return orderedGroup.reduce(
      (acc, r) => {
        if (!acc.roles) {
          acc.roles = [];
        }
        acc.roles.push({
          code: r.role,
          desc: r.roleDesc,
        });
        return acc;
      },
      { ...initial }
    );
  });
}

export default AssociatedPartyProfile;
